<script setup>
import { onMounted, ref } from 'vue';
import { fetchData } from '../../fetchData';
import SkeletonLoader from '../SkeletonLoader.vue';
import Sheet from './../Sheet.vue';
import PriceTargetGridItem from './PriceTargetGridItem.vue';
import ScrollMoreButton from '../ScrollMoreButton.vue';

const props = defineProps({
    productId: {
        type: String,
        required: false,
        default: ''
    },
    linkButton: {
        type: Boolean,
        required: false,
        default: true
    }
});
const priceTargetsMarketsFilter = ref([]);
const priceTargetsFilter = ref({});
const priceTargetsOfTheWeek = ref([]);
const priceTargetsMarketsSymbolFilter = ref([]);
const firstLoading = ref(true);
const getPriceTargetsFilter = async () => {
    try {
        const { result = [] } = await fetchData('/api/v1/price-targets/filters');

        priceTargetsFilter.value = result.status_map ?? {};

        priceTargetsMarketsFilter.value = result.subscribed_abo_product_symbols_map ?? [];
    } catch (error) {
        console.error('Error fetching price targets:', error);
    }
};

const getPriceTargetsOfTheWeek = async () => {
    let url = '/api/v1/price-targets?state[]=In+der+Zielzone&sort[by]=updated_at&sort[order]=desc&page_size=6';
    try {
        if (priceTargetsMarketsSymbolFilter.value.length > 0) {
            priceTargetsMarketsSymbolFilter.value.forEach((symbolId) => {
                url += ` &symbol_id[]=${symbolId}`;
            });
        }

        const { result = [] } = await fetchData(url);

        priceTargetsOfTheWeek.value = result.data;
    } catch (error) {
        console.error('Error fetching price targets:', error);
    }
};

onMounted(async () => {
    await getPriceTargetsFilter();

    if (props.productId) {
        const market = priceTargetsMarketsFilter.value.find((item) => item?.product_id === props.productId);

        priceTargetsMarketsSymbolFilter.value = market?.symbols && typeof market.symbols === 'object' ? Object.keys(market.symbols) : [];
    }

    await getPriceTargetsOfTheWeek();
    firstLoading.value = false;
});
</script>

<template>
    <Sheet>
        <div class="flex flex-auto justify-between items-center mb-4">
            <div class="flex flex-col gap-2">
                <p class="text-md font-bold text-black">{{ $t('current_target_zones') }}</p>
                <p v-show="!linkButton" class="text-xs text-gray-500">{{ $t('current_target_zones_subtitle') }}</p>
            </div>
            <RouterLink
                v-show="linkButton"
                to="/dashboard/price-targets"
                class="text-xxs text-gray-500 hover:text-blue-dark flex justify-center gap-1 border border-gray-300 hover:border-blue-dark px-3 py-1 rounded-lg transition duration-300 h-full"
                style="align-items: center"
            >
                <p class="flex items-center h-full">{{ $t('all_zones') }}</p>
                <i class="fi fi-rr-arrow-right text-xs flex items-center h-full ml-2"></i>
            </RouterLink>
        </div>
        <div v-if="firstLoading">
            <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <SkeletonLoader v-for="n in 8" :key="n" height="180px" />
            </div>
        </div>
        <div v-if="priceTargetsOfTheWeek.length === 0 && !firstLoading">
            <no-item-found :item-name="$t('price_target')" />
        </div>

        <div v-if="priceTargetsOfTheWeek.length > 0" class="relative">
            <div ref="gridRef" data-scroll-container class="overflow-y-scroll 2xl:overflow-y-hidden max-h-[280px] mx-auto w-full responsive-grid scroll-smooth">
                <PriceTargetGridItem v-for="priceTarget in priceTargetsOfTheWeek" :key="priceTarget.id" :price-target="priceTarget" :price-targets-states="priceTargetsFilter" />
            </div>
            <scroll-more-button class="2xl:hidden" />
        </div>
    </Sheet>
</template>

<style scoped>
.responsive-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
    align-items: start;
}
</style>
